import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTool, useDeleteTool } from '../../hooks/useTools';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Badge, Button, Card, CardHeader, ConfirmModal, getStatusVariant } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { getGroupLabel } from '../../utils/tool-groups';
import type { PlatformTool, McpTool } from '../../types';
import { isPlatformEntity } from '../../types';
import BackLink from '../../components/navigation/BackLink';

export default function ToolDetail() {
  const { toolId } = useParams<{ toolId: string }>();
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();

  const { data: tool, isLoading, error } = useTool(toolId);
  const deleteTool = useDeleteTool();

  const [deleteModal, setDeleteModal] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !tool) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load tool</p>
        <Link to={routes.console.spaceTools(spaceId)} className="text-primary-600 hover:text-primary-500 mt-2 inline-block">
          Back to tools
        </Link>
      </div>
    );
  }

  const handleDelete = async () => {
    try {
      await deleteTool.mutateAsync(tool.toolId);
      success('Tool deleted successfully');
      navigate(routes.console.spaceTools(spaceId));
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete tool');
    }
  };

  const isPlatformTool = tool.type === 'platform';
  const platformTool = tool as PlatformTool;
  const mcpTool = tool as McpTool;

  return (
    <div className="space-y-6">
      <BackLink to={routes.console.spaceTools(spaceId)}>Tools</BackLink>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{tool.name}</h1>
            <Badge variant={tool.type === 'platform' ? 'info' : 'warning'}>
              {tool.type}
            </Badge>
            <Badge variant={getStatusVariant(tool.status)}>{tool.status}</Badge>
            {tool.group && (
              <Badge variant="gray">{getGroupLabel(tool.group)}</Badge>
            )}
            {tool.source === 'gateway' && (
              <Badge variant="purple">Gateway</Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{tool.description}</p>
        </div>
        {!isPlatformEntity(tool) && tool.source !== 'gateway' && (
          <Link to={routes.console.spaceToolEdit(spaceId, tool.toolId)}>
            <Button>Edit Tool</Button>
          </Link>
        )}
      </div>

      {/* Tool-specific configuration */}
      {isPlatformTool ? (
        <Card>
          <CardHeader title="Platform Tool Configuration" />
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Handler ID</dt>
              <dd className="mt-1 text-sm text-foreground font-mono">
                {platformTool.handlerId}
              </dd>
            </div>
            {platformTool.handlerConfig && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Handler Config</dt>
                <dd className="mt-1">
                  <pre className="text-sm bg-muted p-4 rounded-md overflow-x-auto">
                    {JSON.stringify(platformTool.handlerConfig, null, 2)}
                  </pre>
                </dd>
              </div>
            )}
          </dl>
        </Card>
      ) : (
        <Card>
          <CardHeader title="MCP Tool Configuration" />
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Server URL</dt>
              <dd className="mt-1 text-sm text-foreground font-mono">
                {mcpTool.serverUrl}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Transport</dt>
              <dd className="mt-1 text-sm text-foreground">{mcpTool.transport}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Remote Tool Name</dt>
              <dd className="mt-1 text-sm text-foreground font-mono">
                {mcpTool.remoteToolName}
              </dd>
            </div>
            {mcpTool.timeoutMs && (
              <div>
                <dt className="text-sm font-medium text-muted-foreground">Timeout</dt>
                <dd className="mt-1 text-sm text-foreground">
                  {mcpTool.timeoutMs}ms
                </dd>
              </div>
            )}
          </dl>
        </Card>
      )}

      {/* Input schema */}
      <Card>
        <CardHeader
          title="Input Schema"
          description="JSON Schema defining the tool's input parameters"
        />
        <pre className="text-sm bg-muted p-4 rounded-md overflow-x-auto">
          {JSON.stringify(tool.inputSchema, null, 2)}
        </pre>
      </Card>

      {/* Metadata */}
      <Card>
        <CardHeader title="Metadata" />
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Tool ID</dt>
            <dd className="mt-1 text-sm text-foreground font-mono">{tool.toolId}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Status</dt>
            <dd className="mt-1">
              <Badge variant={getStatusVariant(tool.status)}>{tool.status}</Badge>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Created</dt>
            <dd className="mt-1 text-sm text-foreground">
              {new Date(tool.createdAt).toLocaleString()}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Updated</dt>
            <dd className="mt-1 text-sm text-foreground">
              {new Date(tool.updatedAt).toLocaleString()}
            </dd>
          </div>
          {tool.source === 'gateway' && tool.gatewayName && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Source</dt>
              <dd className="mt-1 text-sm">
                <Link
                  to={routes.console.spaceToolsGateway(spaceId, tool.gatewayId!)}
                  className="text-primary hover:underline"
                >
                  {tool.gatewayName}
                </Link>
              </dd>
            </div>
          )}
          {tool.group && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Group</dt>
              <dd className="mt-1 text-sm text-foreground">{getGroupLabel(tool.group)}</dd>
            </div>
          )}
          {tool.displayOrder != null && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Display Order</dt>
              <dd className="mt-1 text-sm text-foreground">{tool.displayOrder}</dd>
            </div>
          )}
        </dl>
      </Card>

      {/* Danger zone */}
      {!isPlatformEntity(tool) && tool.source !== 'gateway' && (
        <Card className="border-red-200 dark:border-red-800">
          <CardHeader
            title="Danger Zone"
            description="Irreversible and destructive actions"
          />
          <Button variant="danger" onClick={() => setDeleteModal(true)}>
            Delete Tool
          </Button>
        </Card>
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Tool"
        message={`Are you sure you want to delete "${tool.name}"? Agents using this tool will no longer have access to it. This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteTool.isPending}
      />
    </div>
  );
}
